import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Plus, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { KARMA_POINTS, ACHIEVEMENTS } from "@/utils/achievements";
import { MediaUploader } from "./post/MediaUploader";
import { TagsInput } from "./post/TagsInput";
import { PrivacySettings } from "./post/PrivacySettings";
import { CategorySelector } from "./post/CategorySelector";
import { MentionSuggestions } from "./post/MentionSuggestions";

interface CreatePostDialogProps {
  openByDefault?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  groupId?: string;
  onPostCreated?: () => void;
}

const categories = [
  { value: "general", label: "General" },
  { value: "gratitude", label: "Gratitude" },
  { value: "support", label: "Support" },
  { value: "encouragement", label: "Encouragement" }
];

export const CreatePostDialog = ({ 
  openByDefault = false, 
  open: controlledOpen, 
  onOpenChange: controlledOnOpenChange,
  groupId,
  onPostCreated
}: CreatePostDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(openByDefault);
  const open = controlledOpen ?? internalOpen;
  const onOpenChange = (value: boolean) => {
    setInternalOpen(value);
    if (controlledOnOpenChange) {
      controlledOnOpenChange(value);
    }
  };
  const navigate = useNavigate();

  const [content, setContent] = useState("");
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mediaFiles, setMediaFiles] = useState<Array<{ url: string; type: "image" | "video" }>>([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState(categories[0].value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleMediaUpload = (preview: string | null, type: "image" | "video" | null) => {
    if (preview && type) {
      setMediaFiles(prev => [...prev, { url: preview, type }]);
    }
  };

  const handleRemoveMedia = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTag = (tag: string) => {
    setCustomTags([...customTags, tag]);
  };

  const handleRemoveTag = (tag: string) => {
    setCustomTags(customTags.filter(t => t !== tag));
  };


  const handleMentionSelect = (username: string) => {
    const textBeforeCursor = content.slice(0, textareaRef.current?.selectionStart || 0);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = content.slice(textareaRef.current?.selectionStart || 0);
    
    setContent(
      textBeforeCursor.slice(0, lastAtSymbol) + 
      '@' + username + ' ' + 
      textAfterCursor
    );
    setMentionSearch("");
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);

    const textBeforeCursor = newContent.slice(0, e.target.selectionStart);
    const match = textBeforeCursor.match(/@(\w*)$/);
    
    if (match) {
      const rect = e.target.getBoundingClientRect();
      const position = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
      
      if (position) {
        setMentionPosition({
          top: position.bottom - rect.top,
          left: position.left - rect.left
        });
      }
      
      setMentionSearch(match[1]);
    } else {
      setMentionSearch("");
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) {
        toast({
          title: "Error",
          description: "You must be logged in to create a post.",
          variant: "destructive",
        });
        return;
      }

      const userId = session.session.user.id;

      const { data: postData, error: postError } = await supabase
        .from('posts')
        .insert([{
          user_id: userId,
          content,
          category: groupId ? 'group' : selectedCategory, // Use 'group' category for group posts
          is_anonymous: isAnonymous,
          is_private: isPrivate,
          custom_tags: customTags,
          image_url: mediaFiles.find(f => f.type === "image")?.url || null,
          video_url: mediaFiles.find(f => f.type === "video")?.url || null,
          created_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (postError) throw postError;

      if (groupId && postData) {
        const { error: groupPostError } = await supabase
          .from('group_posts')
          .insert([{
            group_id: groupId,
            post_id: postData.id,
            status: 'approved', // Set to approved to fix permission issues
            category: 'discussion',
            tags: customTags
          }]);

        if (groupPostError) throw groupPostError;
      }

      // Try to update karma, but continue even if it fails due to permissions
      try {
        await supabase
          .from('user_karma_history')
          .insert([{
            user_id: userId,
            action_type: 'post_created',
            points: KARMA_POINTS.CREATE_POST,
            description: 'Created a new post'
          }]);

        await supabase.rpc('update_user_karma', {
          user_id: userId,
          points_to_add: KARMA_POINTS.CREATE_POST,
          karma_type: 'post_points'
        });
      } catch (karmaError) {
        console.error('Karma update error (non-critical):', karmaError);
        // Continue execution even if karma update fails
      }

      // Reset form
      setContent("");
      setMediaFiles([]);
      setCustomTags([]);
      setIsAnonymous(false);
      setIsPrivate(false);

      toast({
        title: "Post added! 🎉",
        description: "Your post has been shared successfully.",
        duration: 3000,
      });

      // Always close the dialog after submission
      onOpenChange(false);
      
      // Call onPostCreated callback if provided
      if (onPostCreated) {
        onPostCreated();
      }
      
      // If we're on the CreatePost page, navigate back
      if (openByDefault) {
        navigate('/');
      }

    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "Failed to create post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!controlledOpen && (
        <DialogTrigger asChild>
          <Button 
            variant="default" 
            size="icon" 
            className="rounded-full bg-gradient-to-r from-purple-400 via-pink-500 to-red-500 hover:from-purple-500 hover:via-pink-600 hover:to-red-600 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <Plus className="w-6 h-6 text-white" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto bg-gradient-to-b from-white to-purple-50 dark:from-gray-900 dark:to-purple-900/20 mx-4 w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {groupId ? "Share with Group" : "Share a moment"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            ref={textareaRef}
            placeholder="What's happening? Share a life update or moment..."
            value={content}
            onChange={handleContentChange}
            className="min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
          />

          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={setSelectedCategory}
          />

          {mediaFiles.length > 0 && (
            <div className="space-y-4">
              {mediaFiles.map((file, index) => (
                <div key={index} className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
                  {file.type === "image" ? (
                    <img src={file.url} alt="Preview" className="object-contain w-full h-full" />
                  ) : (
                    <video src={file.url} controls className="w-full h-full">
                      <track kind="captions" />
                    </video>
                  )}
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={() => handleRemoveMedia(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="relative">
            {mentionSearch && (
              <MentionSuggestions
                searchTerm={mentionSearch}
                onSelect={handleMentionSelect}
                position={mentionPosition}
              />
            )}
            <MediaUploader onMediaUpload={handleMediaUpload} />
          </div>

          <PrivacySettings
            isAnonymous={isAnonymous}
            isPrivate={isPrivate}
            onAnonymousChange={setIsAnonymous}
            onPrivateChange={setIsPrivate}
          />

          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting || !content.trim()}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sharing...
              </>
            ) : (
              groupId ? 'Share with Group' : 'Share Post'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
